import { RecruitmentMarketingProvider } from '@/utils/types';

const CONNECT_REDIRECT_KEY = 'marketingConnectRedirectLocation';

export const saveConnectRedirectLocation = (location: string): void => {
  localStorage.setItem(CONNECT_REDIRECT_KEY, location);
};

// Non-destructive for the same reason as `peekReconnectConnectionId`: a
// remounted callback must still find where to send the user back to.
export const peekConnectRedirectLocation = (): string | null =>
  localStorage.getItem(CONNECT_REDIRECT_KEY) || null;

export const clearConnectRedirectLocation = (): void => {
  localStorage.removeItem(CONNECT_REDIRECT_KEY);
};

const RECONNECT_ID_KEY = 'marketingReconnectConnectionId';

export const saveReconnectConnectionId = (connectionId: string): void => {
  localStorage.setItem(RECONNECT_ID_KEY, connectionId);
};

// Deliberately non-destructive: the caller reads this during render, which is
// not guaranteed to happen exactly once (discarded renders, remounts). Clearing
// here would drop the id before the reconnect request is ever sent, silently
// downgrading the reconnect to a fresh connect.
export const peekReconnectConnectionId = (): string | null =>
  localStorage.getItem(RECONNECT_ID_KEY) || null;

export const clearReconnectConnectionId = (): void => {
  localStorage.removeItem(RECONNECT_ID_KEY);
};

const SENT_AUTHORIZATION_CODE_KEY = 'marketingSentAuthorizationCode';

// Authorization codes are single-use, so a code is recorded before it is sent:
// a callback reloaded mid-request (e.g. by an Auth0 login redirect) must not
// replay it into a guaranteed failure.
export const markAuthorizationCodeSent = (authorizationCode: string): void => {
  localStorage.setItem(SENT_AUTHORIZATION_CODE_KEY, authorizationCode);
};

export const wasAuthorizationCodeSent = (authorizationCode: string): boolean =>
  localStorage.getItem(SENT_AUTHORIZATION_CODE_KEY) === authorizationCode;

export const CONNECTION_ID_PARAM = 'connectionId';
export const CONNECTION_PROVIDER_PARAM = 'connectionProvider';

/**
 * Hands the connection back to the page the user left. The provider rides along
 * because the return location is provider-agnostic — the page it lands on has to
 * know which integration just finished to expand the right card and, in the
 * wizard, to re-select the matching tracking method.
 */
export const appendConnectionResult = (
  location: string,
  connectionId: string,
  provider: RecruitmentMarketingProvider
): string => {
  const url = new URL(location, window.location.origin);
  url.searchParams.set(CONNECTION_ID_PARAM, connectionId);
  url.searchParams.set(CONNECTION_PROVIDER_PARAM, provider);
  return `${url.pathname}${url.search}`;
};

export const getConnectionId = (search: string): string | null =>
  new URLSearchParams(search).get(CONNECTION_ID_PARAM);

export const getConnectionProvider = (
  search: string
): RecruitmentMarketingProvider | null => {
  const provider = new URLSearchParams(search).get(CONNECTION_PROVIDER_PARAM);

  return provider === 'meta' || provider === 'google' ? provider : null;
};
